import {
  LogIn,
  LayoutDashboard,
  FileText,
  CalendarPlus,
  Clock,
  UserPen,
  Bell,
  Lock,
  Receipt,
  CalendarDays,
  Wallet,
  UserCircle,
  HeartHandshake,
  Sparkles,
} from "lucide-react";
import FeaturePageLayout from "./FeaturePageLayout";
import essHeroImg from "../../assets/images/features/Employee-Self-Service-hero.png.png";

const workflowSteps = [
  {
    id: 1,
    title: "Secure Employee Login",
    desc: "Employees access Brixigo ESS with role-based authentication — only their own data, never another employee's records.",
    icon: LogIn,
  },
  {
    id: 2,
    title: "Personal HR Dashboard",
    desc: "One screen for profile, attendance summary, leave balances, latest payslip and pending approvals.",
    icon: LayoutDashboard,
  },
  {
    id: 3,
    title: "Self-Service Actions",
    desc: "Download payslips, apply for leave and view working hours without emailing HR for every request.",
    icon: FileText,
  },
  {
    id: 4,
    title: "Real-Time Status",
    desc: "Leave approval status, attendance logs and payroll history update as HR processes transactions.",
    icon: Bell,
  },
  {
    id: 5,
    title: "Controlled Profile Updates",
    desc: "Employees update permitted profile fields; sensitive changes remain with HR per organisation policy.",
    icon: UserPen,
  },
];

const keyBenefits = [
  { title: "View payslips", desc: "Download current and past payslips anytime from the portal.", icon: Receipt },
  { title: "Apply for leave", desc: "Submit leave requests and track approval from ESS.", icon: CalendarPlus },
  { title: "Attendance history", desc: "See clock-ins, hours and attendance status for transparency.", icon: Clock },
  { title: "Payroll information", desc: "Salary components and deduction summaries at a glance.", icon: Wallet },
  { title: "Personal profile management", desc: "Update allowed contact and profile details securely.", icon: UserCircle },
  { title: "Leave balance tracking", desc: "Remaining CL, SL, EL and other balances always visible.", icon: CalendarDays },
  { title: "Secure employee portal", desc: "Encrypted access with organisation-scoped data isolation.", icon: Lock },
  { title: "Improved employee experience", desc: "Less back-and-forth with HR — answers available 24/7.", icon: HeartHandshake },
];

const whatsIncluded = [
  { title: "My Payslips", desc: "PDF payslips for every processed payroll period.", icon: Receipt },
  { title: "Leave Self-Service", desc: "Apply, view history and cancel pending requests.", icon: CalendarPlus },
  { title: "My Attendance", desc: "Daily logs and monthly attendance calendar view.", icon: Clock },
  { title: "Tax & Form 16", desc: "Access tax documents where enabled by your organisation.", icon: FileText },
  { title: "Profile & Settings", desc: "View and edit permitted personal information.", icon: UserPen },
  { title: "Approval Notifications", desc: "See when leave or other requests are approved.", icon: Bell },
  { title: "Mobile-Friendly Access", desc: "ESS works on desktop and mobile browsers.", icon: LayoutDashboard },
  { title: "RBAC Security", desc: "Employees see only their own HR and payroll data.", icon: Lock },
];

export default function EmployeeSelfService() {
  return (
    <FeaturePageLayout
      featureName="Employee Self Service"
      title="Employee Self Service"
      subtitle="One secure portal for payslips, leave, attendance and profile — built into Brixigo."
      description="Employee Self Service (ESS) lets employees access HR and payroll information anytime. View payslips, apply for leave, check attendance and update profile details from a single dashboard — improving transparency and reducing routine HR queries."
      heroImage={essHeroImg}
      heroImageAlt="Employee self service portal illustration"
      workflowHeading="How Employee Self Service Works in Brixigo"
      workflowSteps={workflowSteps}
      benefits={keyBenefits}
      included={whatsIncluded}
      includedHeading="Portal Capabilities"
      ctaIcon={Sparkles}
      ctaTitle="Empower Employees. Free Up HR."
      ctaDescription="Give your team self-service access to payslips, leave and attendance — securely and on their schedule."
      ctaButtonLabel="Book a Demo"
    />
  );
}
